import * as React from 'react';
import styled from '@emotion/styled';
import FlightSwitch from '../../components/FlightSwitch/FlightSwitch';
import {
    CheckmarkList,
    CheckmarkListItem,
    CheckmarkListItemIcon,
    CheckmarkListItemText,
} from '../../components/CheckmarkList/CheckmarkList';
import { config } from '../../config';
import { __ } from '../../helpers/TranslationService';
import { useTypedSelector } from '../../hooks/useTypedSelector';
import { IFormattedCabin, ISoldOutBooking } from '../../interfaces';
import { IExchangeRate } from '../../interfaces/IHeader';
import PerPersonPriceOverview from '../PerPersonPriceOverview/PerPersonPriceOverview';
import { PerPersonPriceTable } from '../PerPersonPriceTable/PerPersonPriceTable';
import ScrollToBtn from '../ScrollToBtn/ScrollToBtn';
import { overviewPriceHelper } from './overviewPriceHelper';
import { SHIP_IN_SHIP_CABIN } from '../../reducers/cruisePage/cruisePageMappers';

export interface ICabineTypePriceLine {
    cabinName: string;
    cab: IFormattedCabin;
    departure: number;
    soldOutBooking: ISoldOutBooking;
    currency: string;
    isDualCurrency: boolean;
    exchangeRate: IExchangeRate;
    currentCurrency: string;
    operator: string;
}

export interface IProps {
    startHarbour?: string;
    endHarbour?: string;
    setActiveCruisePageTab?: (value: number) => void;
}

export const CheckmarkListItemTextStyled = styled(CheckmarkListItemText)`
    font-weight: 600;
`;

const ButtonsWrapper = styled.div`
    display: flex;
    gap: 8px;
    margin-top: 12px;
`;

const compName = 'overview-price-component';
const REQUEST_FORM_CONTAINER_ANCHOR = '.requestFormContainer';

const PriceTable = (props: IProps) => {
    const { cruisePage, pageData } = useTypedSelector((state) => state);
    const {
        currency,
        featureBulletPoints,
        soldOutBooking,
        sails,
        selectedSailIndex,
        loadingRates,
        includesFlight,
        alternativeCruise,
    } = cruisePage;

    const onCabinTypePriceClick = () => {
        const anchor = document.querySelector(REQUEST_FORM_CONTAINER_ANCHOR);
        if (anchor) {
            anchor.scrollIntoView({
                behavior: 'smooth',
            });
        }
    };

    React.useEffect(() => {
        // We need to set it to 'undefined' on unmount because we use activeTabIndex
        // as a dependency inside 'CruisePageTabs' component to prevent the same value in a state
        // when we click 'Show Itinerary' a second time
        return () => props.setActiveCruisePageTab(undefined);
    }, []);

    const { exchangeRate } = pageData.header;

    const cabins = cruisePage?.processedCabins;

    const { startHarbour, endHarbour } = props;
    const isAlternativeCruise = alternativeCruise !== undefined;
    const alternativeCruiseUrl = alternativeCruise?.url;

    const isDesktop = pageData.deviceType === 'desktop';

    const isDualCurrency = exchangeRate !== null;
    const currentCurrency = isDualCurrency ? __('currency', 'dreamlines') : currency;
    const cabinPrices = !cabins
        ? []
        : Object.entries(cabins).filter(
              ([key]) => cabins[key].cabins?.length > 0 && key !== 'withoutPrice',
          );
    const selectedSail = sails[selectedSailIndex];
    const departure = selectedSail?.departure || undefined;

    return (
        <div className={`${compName} `}>
            <CheckmarkList>
                {featureBulletPoints.map((feature, i) => (
                    <CheckmarkListItem key={`feature-${i}`}>
                        <CheckmarkListItemIcon />
                        <CheckmarkListItemTextStyled>{feature}</CheckmarkListItemTextStyled>
                    </CheckmarkListItem>
                ))}
            </CheckmarkList>
            <div className={`${compName}__placeholder ${!loadingRates ? 'hidden' : ''}`}>
                <div className={`${compName}__placeholder__loading`}>
                    <img
                        className="mapMarker"
                        alt="Map Marker"
                        src={`${config.images}/common/loading.gif`}
                    />
                </div>
                <span className={`${compName}__placeholder_please-wait`}>
                    {__('buttons.pleaseWait', 'dreamlines')}
                </span>
            </div>
            {isAlternativeCruise && (
                <FlightSwitch
                    startHarbour={startHarbour}
                    endHarbour={endHarbour}
                    includesFlight={includesFlight}
                    url={alternativeCruiseUrl}
                    isDesktop={isDesktop}
                />
            )}

            {!!cabinPrices?.length && !loadingRates && (
                <div className={`${compName}__slected-price`}>
                    <>
                        <PerPersonPriceOverview {...overviewPriceHelper(pageData, cruisePage)} />
                        <ButtonsWrapper>
                            <ScrollToBtn
                                title={__('Dates and Prices', 'dreamlines')}
                                anchorSelector={REQUEST_FORM_CONTAINER_ANCHOR}
                                disabled={cruisePage.sails.length === 0}
                            />
                        </ButtonsWrapper>
                    </>
                </div>
            )}

            {!loadingRates && (
                <div className={`${compName}__table-holder`}>
                    <table className={`${compName}__cabin-options`} onClick={onCabinTypePriceClick}>
                        <tbody>
                            {cabinPrices.map(([cabinName, cab], i) => {
                                return (
                                    <CabineTypePriceLine
                                        key={`cabin-type-${i}`}
                                        cabinName={cabinName}
                                        cab={cab}
                                        departure={departure}
                                        soldOutBooking={soldOutBooking}
                                        currency={currency}
                                        isDualCurrency={isDualCurrency}
                                        exchangeRate={exchangeRate}
                                        currentCurrency={currentCurrency}
                                        operator={cruisePage.company.bookingServiceCode}
                                    />
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

const CabineTypePriceLine = (props: ICabineTypePriceLine) => {
    const { cabinName, cab } = props;
    const checkCabin = cab.cabins.filter((cabin) => cabin.cabinType.nid === cab.cabinNid)[0];

    const displayCabinName =
        cabinName === SHIP_IN_SHIP_CABIN ? `shipCabin${props.operator.toUpperCase()}` : cabinName;

    let referenceCabin =
        typeof checkCabin === 'undefined'
            ? cab.cabins.filter((cabin) => cabin.cabinType.nid === cab.cabinNid)[0]
            : cab.cabins[0];

    if (typeof referenceCabin === 'undefined') {
        return null;
    }
    return (
        <tr className={`${compName}__cabin-option`} data-testid="cabin-option">
            <td className={`${compName}__cabin-option__title`}>
                {__(displayCabinName, 'dreamlines')}
            </td>
            <td
                className={`${compName}__cabin-option__extra`}
                data-testid="cabin-option-old-price"
                title={__('catalogPrice', 'dreamlines')}
            >
                <PerPersonPriceTable
                    priceLabel={referenceCabin.cabinType.labels.priceTable.labelOldMainCurrency}
                    dualCurrencyString={
                        referenceCabin.cabinType.labels.priceTable.labelOldDualCurrency
                    }
                />
            </td>
            <td
                className={`${compName}__cabin-option__value`}
                data-testid="cabin-option-price"
                title={__('currentPrice', 'dreamlines')}
            >
                <PerPersonPriceTable
                    priceLabel={referenceCabin.cabinType.labels.priceTable.labelMainCurrency}
                    dualCurrencyString={
                        referenceCabin.cabinType.labels.priceTable.labelDualCurrency
                    }
                />
            </td>
        </tr>
    );
};
export default PriceTable;
